import { useState, type CSSProperties } from 'react'
import { avatarGradient, getInitials, levelBadgeSizeFor } from './avatar-utils'
import { cn } from './cn'
import { LevelBadge } from './LevelBadge'
import { t } from '@/lib/i18n'

export type AvatarSize = 24 | 28 | 32 | 40 | 48 | 64 | 96 | 128

export interface AvatarProps {
  /** Display name, used for initials and the accessible label. */
  name?: string | null
  /** Image URL; falls back to initials on a neutral cool gray picked by id. */
  src?: string | null
  /** User id that picks the fallback tone (falls back to the name). */
  id?: string | number | null
  size?: AvatarSize | number
  level?: number | null
  online?: boolean
  /** 2px surface ring, used in stacks and over covers. */
  ring?: boolean
  alt?: string
  className?: string
  style?: CSSProperties
}

/** Circular user avatar with lazy image, initials fallback, level badge and online dot. */
export function Avatar({ name, src, id, size = 40, level, online, ring, alt, className, style }: AvatarProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
  const showImage = Boolean(src) && failedSrc !== src
  const loaded = showImage && loadedSrc === src
  const initials = getInitials(name, size >= 32 ? 2 : 1)
  const label = alt ?? (name ? (level ? t('{name}, level {level}', { name, level }) : name) : t('User'))
  const fontSize = Math.max(10, Math.round(size * (initials.length > 1 ? 0.38 : 0.44)))
  return (
    <span
      role="img"
      aria-label={label}
      className={cn('relative inline-flex shrink-0 rounded-full align-middle select-none', className)}
      style={{ width: size, height: size, ...style }}
      data-size={size}
    >
      <span
        className={cn('relative flex size-full items-center justify-center overflow-hidden rounded-full', ring && 'ring-2 ring-surface')}
        style={{ background: loaded ? undefined : avatarGradient(id ?? name ?? '') }}
      >
        {!loaded && (
          <span aria-hidden="true" className="font-semibold leading-none tracking-[0.01em] text-white" style={{ fontSize }}>
            {initials}
          </span>
        )}
        {showImage && (
          <img
            src={src ?? undefined}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            ref={(img) => {
              if (img?.complete && img.naturalWidth > 0 && loadedSrc !== src) setLoadedSrc(src ?? null)
            }}
            onLoad={() => setLoadedSrc(src ?? null)}
            onError={() => setFailedSrc(src ?? null)}
            className={cn(
              'absolute inset-0 size-full object-cover transition-opacity duration-150',
              loaded ? 'opacity-100' : 'opacity-0',
            )}
          />
        )}
      </span>
      {online && (
        <span
          aria-hidden="true"
          className="absolute bottom-0 left-0 size-2.5 rounded-full bg-success ring-2 ring-surface"
          style={size >= 64 ? { left: size * 0.08, bottom: size * 0.08 } : undefined}
        />
      )}
      {level ? (
        <LevelBadge level={level} size={levelBadgeSizeFor(size)} decorative className="absolute -right-[3px] -bottom-[3px]" />
      ) : null}
    </span>
  )
}
